import type { BoardType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import { isTeamBoard } from "@/model/board";
import type { Card } from "@/model/card";
import { isBacklogWorkitem, isDependency, isRisk } from "@/model/stickyType";
import { useBoardsStore } from "@/store/boards";
import { useServerSettingsStore } from "@/store/serverSettings";

type ActionMenuAction =
  | "almSource"
  | "delete"
  | "remove"
  | "program"
  | "team"
  | "art"
  | "mirror"
  | "move"
  | "depend"
  | "link"
  | "activity"
  | "breakdown";

const baseActions: ActionMenuAction[] = ["delete", "almSource", "mirror", "activity", "breakdown"];
const actions: Record<BoardType, ActionMenuAction[]> = {
  solution_backlog: [...baseActions, "art", "link"],
  solution: [...baseActions, "link"],
  backlog: [...baseActions, "team", "link"],
  program: [...baseActions, "link"],
  risk: [...baseActions, "link"],
  flex: ["delete", "activity"],
  team: [],
  objectives: [],
};

const baseTeamActions: ActionMenuAction[] = [
  "delete",
  "almSource",
  "move",
  "link",
  "activity",
  "breakdown",
];
const teamActions: Record<string, ActionMenuAction[]> = {
  default: [...baseTeamActions, "mirror"],
  risk: [...baseTeamActions, "mirror"],
  depend: [...baseTeamActions, "program"],
  program: [...baseTeamActions, "depend"],
};

const cardWasCreatedOnDifferentTeamBoard = (card: Card, board: Board) => {
  return card.precondTeam && (board.type !== "team" || card.precondTeam.id !== board.team.id);
};

export const cardWasCreatedOnDifferentBoard = (card: Card, board: Board) => {
  return card.type.origin !== board.type || cardWasCreatedOnDifferentTeamBoard(card, board);
};

/**
 * Actions that a sticky note can have depending on the board type
 */
const getActionsByBoardType = (card: Card, board: Board): ActionMenuAction[] => {
  return isTeamBoard(board.type) ? teamBoardActions(card) : actions[board.type];
};

const teamBoardActions = (card: Card): ActionMenuAction[] => {
  switch (card.type.functionality) {
    case "risk":
      return teamActions.risk;
    case "dependency":
      return card.dependTeam ? teamActions.depend : teamActions.program;
    default:
      return teamActions.default;
  }
};

export const isStickyMovable = (card: Card, board: Board) => {
  const isOnOriginBoard = card.type.origin === board.type; // sticky note was created on this board
  const moveBetweenTeams = useServerSettingsStore().moveBetweenTeams; // is allowed to be moved in the cockpit

  return isOnOriginBoard && (moveBetweenTeams || isRisk(card)) && !isDependency(card);
};

export const isStickyMirrorable = (card: Card) => {
  return useBoardsStore().mirrorTargetBoards([card]).length;
};

/** Cards that can be broken down. */
export const isBreakdownTarget = (card: Card, board: Board): boolean =>
  (isBacklogWorkitem(card) || isDependency(card)) && isActionVisible("breakdown", card, board);

/**
 * Determines if specific action should be visible in the action menu
 */
export const isActionVisible = (action: ActionMenuAction, card: Card, board: Board): boolean => {
  if (!card || !board) return false;
  return getActionsByBoardType(card, board).includes(action);
};
